import { RefObject, useEffect, useRef, useState } from 'react';

import { Text } from '@thefarmersfront/kitchen';

import { ScaleBand, ScaleLinear, scaleBand } from 'd3-scale';
import { line } from 'd3-shape';

import { ChartLayout } from '~/components/common/chart/core/ChartLayout';
import { useChartTooltip } from '~/components/common/chart/core/ChartTooltip';
import { YAxisGrid } from '~/components/common/chart/core/YAxisGrid';
import { convertSvgToScreen } from '~/components/common/chart/core/utils';

import { createEvenScale, getLeftYAxisTicks, getRightYAxisTicks, rectPath, topRoundedRectPath } from './utils';

import { DeliveryAgencyDeliveryStatusTrendTooltip } from './DeliveryAgencyDeliveryStatusTrendTooltip';
import { LABEL_Y_OFFSET, LEGEND_ITEMS, MARGIN, TICK_WIDTH, Y_LABEL_X_OFFSET, Y_LABEL_Y_OFFSET } from './constant';

import type { TypeDailyTrend } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyDeliveryStatusTrendChart.module.scss';

const CIRCLE_SIZE = 12;

const INNER_CIRCLE_SIZE = 6;

interface BarBounds {
  x: number;
  yTop: number;
  yBottom: number;
  item: TypeDailyTrend;
}

interface TooltipData {
  item: TypeDailyTrend;
  leftX: number;
}

interface RateLineSeriesProps {
  xScale: ScaleBand<string>;
  yScale: ScaleLinear<number, number>;
  data: { date: string; value: number }[];
  color: string;
  hoveredDate?: string | null;
  chartRef: RefObject<SVGSVGElement>;
  barBoundsByDate: Map<string, BarBounds>;
  onShowTooltip: (position: { x: number; y: number }, data: TooltipData) => void;
  onHideTooltip: () => void;
}

const RateLineSeries = ({
  xScale,
  yScale,
  data,
  color,
  hoveredDate,
  chartRef,
  barBoundsByDate,
  onShowTooltip,
  onHideTooltip,
}: RateLineSeriesProps) => {
  const bandW = xScale.bandwidth();

  const lineGenerator = line<{ date: string; value: number }>()
    .x((d) => (xScale(d.date) ?? 0) + bandW / 2)
    .y((d) => yScale(d.value));

  const path = lineGenerator(data) ?? '';

  return (
    <g>
      <path d={path} fill="none" stroke={color} strokeWidth="1" />
      {data.map((item) => {
        const cx = (xScale(item.date) ?? 0) + bandW / 2;
        const cy = yScale(item.value);
        const isHovered = item.date === hoveredDate;
        const bar = barBoundsByDate.get(item.date);

        const handleMouseEnter = () => {
          if (!bar) return;

          const overlapsBar = cy >= bar.yTop && cy <= bar.yBottom;
          const tooltipY = overlapsBar ? bar.yTop : cy;
          const screenPos = convertSvgToScreen(chartRef.current, bar.x + TICK_WIDTH, tooltipY);
          const screenLeftPos = convertSvgToScreen(chartRef.current, bar.x, tooltipY);

          onShowTooltip(screenPos, { item: bar.item, leftX: screenLeftPos.x });
        };

        return (
          <g key={item.date}>
            <circle
              cx={cx}
              cy={cy}
              r={CIRCLE_SIZE / 2}
              fill={color}
              style={{ cursor: 'pointer' }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={onHideTooltip}
            />
            {isHovered && <circle cx={cx} cy={cy} r={INNER_CIRCLE_SIZE / 2} fill="#fff" pointerEvents="none" />}
          </g>
        );
      })}
    </g>
  );
};

interface StackedBandSeriesProps {
  xScale: ScaleBand<string>;
  yScale: ScaleLinear<number, number>;
  data: TypeDailyTrend[];
  chartRef: React.RefObject<SVGSVGElement>;
  onShowTooltip: (position: { x: number; y: number }, data: TooltipData) => void;
  onHideTooltip: () => void;
}

const LABEL_ROTATE_THRESHOLD = 40;

const StackedBandSeries = ({
  xScale,
  yScale,
  data,
  chartRef,
  onShowTooltip,
  onHideTooltip,
}: StackedBandSeriesProps) => {
  const bandW = xScale.bandwidth();
  const shouldRotate = bandW < LABEL_ROTATE_THRESHOLD;

  return (
    <g>
      {data.map((item) => {
        const bandX = xScale(item.deliveryDate) ?? 0;
        const x = bandX + (bandW - TICK_WIDTH) / 2;

        const nightCount = item.nightAcceptedCount ?? 0;
        const total = nightCount + item.dawnAcceptedCount + item.dayAcceptedCount;
        const yBottom = yScale(0);
        const yDay = yScale(item.dayAcceptedCount);
        const yDayDawn = yScale(item.dayAcceptedCount + item.dawnAcceptedCount);
        const yTop = yScale(total);

        const label = item.deliveryDate.slice(5).replace('-', '.');
        const labelX = bandX + bandW / 2 + (shouldRotate ? 4 : 0);
        const labelY = yBottom + (shouldRotate ? LABEL_Y_OFFSET / 2 : LABEL_Y_OFFSET);

        return (
          <g key={item.deliveryDate}>
            <g
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => {
                const screenPos = convertSvgToScreen(chartRef.current, x + TICK_WIDTH, yTop);
                const screenLeftPos = convertSvgToScreen(chartRef.current, x, yTop);
                onShowTooltip(screenPos, { item, leftX: screenLeftPos.x });
              }}
              onMouseLeave={onHideTooltip}
            >
              {yBottom > yDay && <path d={rectPath(x, yDay - 1, yBottom)} fill="#5094DC" />}
              {yDay > yDayDawn && <path d={rectPath(x, yDayDawn - 1, yDay)} fill="#BD76FF" />}
              {yDayDawn > yTop && <path d={topRoundedRectPath(x, yTop, yDayDawn)} fill="#4EB595" />}
            </g>

            <text
              x={labelX}
              y={labelY}
              textAnchor={shouldRotate ? 'end' : 'middle'}
              fill="#000"
              fontSize="14px"
              fontWeight="600"
              {...(shouldRotate && { transform: `rotate(-45, ${labelX}, ${labelY})` })}
            >
              {label}
            </text>
          </g>
        );
      })}
    </g>
  );
};

interface DeliveryAgencyDeliveryStatusTrendChartProps {
  data: TypeDailyTrend[];
}

const DeliveryAgencyDeliveryStatusTrendChart = ({ data }: DeliveryAgencyDeliveryStatusTrendChartProps) => {
  const maxCount = Math.max(0, ...data.map((d) => (d.nightAcceptedCount ?? 0) + d.dawnAcceptedCount + d.dayAcceptedCount));
  const maxRate = Math.max(0, ...data.map((d) => Math.max(d.delayedRate, d.failedRate)));
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<SVGSVGElement>(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const { tooltipState, showTooltip, hideTooltip } = useChartTooltip<TooltipData>();

  useEffect(() => {
    if (!chartAreaRef.current) return;

    let rafId: number;
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setChartSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      });
    });
    observer.observe(chartAreaRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  const leftTicks = getLeftYAxisTicks(maxCount);
  const rightTicks = getRightYAxisTicks(maxRate);

  return (
    <div className={styles.container}>
      <div ref={chartAreaRef} className={styles['chart-area']}>
        {chartSize.width > 0 && chartSize.height > 0 && (
          <ChartLayout ref={chartRef} width={chartSize.width} height={chartSize.height} margin={MARGIN}>
            {({ innerWidth, innerHeight }) => {
              const leftYScale = createEvenScale(leftTicks, innerHeight);
              const rightYScale = createEvenScale(rightTicks, innerHeight);

              const xScale = scaleBand()
                .domain(data.map((d) => d.deliveryDate))
                .range([0, innerWidth])
                .paddingInner(0.3)
                .paddingOuter(0.1);

              const bandW = xScale.bandwidth();
              const yBottom = leftYScale(0);
              const barBoundsByDate = new Map<string, BarBounds>();
              data.forEach((item) => {
                const bandX = xScale(item.deliveryDate) ?? 0;
                const x = bandX + (bandW - TICK_WIDTH) / 2;
                const total = (item.nightAcceptedCount ?? 0) + item.dawnAcceptedCount + item.dayAcceptedCount;
                const yTop = leftYScale(total);
                barBoundsByDate.set(item.deliveryDate, { x, yTop, yBottom, item });
              });

              return (
                <>
                  <YAxisGrid
                    yScale={leftYScale}
                    ticks={leftTicks}
                    x={{ start: 0, end: innerWidth }}
                    labelOffset={{ x: Y_LABEL_X_OFFSET, y: Y_LABEL_Y_OFFSET }}
                  />
                  <YAxisGrid
                    yScale={rightYScale}
                    ticks={rightTicks}
                    x={{ start: innerWidth, end: innerWidth }}
                    side="right"
                    labelOffset={{ x: Y_LABEL_X_OFFSET, y: Y_LABEL_Y_OFFSET }}
                    labelFormat={(tick) => `${tick}%`}
                    getTickProps={() => ({ strokeWidth: 0 })}
                  />
                  <StackedBandSeries
                    xScale={xScale}
                    yScale={leftYScale}
                    data={data}
                    chartRef={chartRef}
                    onShowTooltip={showTooltip}
                    onHideTooltip={hideTooltip}
                  />
                  <RateLineSeries
                    xScale={xScale}
                    yScale={rightYScale}
                    data={data.map((d) => ({ date: d.deliveryDate, value: d.delayedRate }))}
                    color="#F1C236"
                    hoveredDate={tooltipState?.data.item.deliveryDate}
                    chartRef={chartRef}
                    barBoundsByDate={barBoundsByDate}
                    onShowTooltip={showTooltip}
                    onHideTooltip={hideTooltip}
                  />
                  <RateLineSeries
                    xScale={xScale}
                    yScale={rightYScale}
                    data={data.map((d) => ({ date: d.deliveryDate, value: d.failedRate }))}
                    color="#E22D2E"
                    hoveredDate={tooltipState?.data.item.deliveryDate}
                    chartRef={chartRef}
                    barBoundsByDate={barBoundsByDate}
                    onShowTooltip={showTooltip}
                    onHideTooltip={hideTooltip}
                  />
                </>
              );
            }}
          </ChartLayout>
        )}
      </div>

      {tooltipState?.isVisible && (
        <DeliveryAgencyDeliveryStatusTrendTooltip
          top={tooltipState.position.y}
          left={tooltipState.position.x}
          leftX={tooltipState.data.leftX}
          item={tooltipState.data.item}
        />
      )}

      <div className={styles.legend}>
        {LEGEND_ITEMS.map((item) => (
          <div key={item.text} className={styles['legend-item']}>
            <span className={styles['legend-color']} style={{ backgroundColor: item.color }} />
            <Text size="large" weight="regular">
              {item.text}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

export { DeliveryAgencyDeliveryStatusTrendChart };
